import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, Button, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'EN', fullName: 'English' },
  // { code: 'hi', label: 'HI', fullName: 'हिंदी' },
] as const;

const DEFAULT_LANGUAGE = { code: 'en', label: 'EN', fullName: 'English' } as const;

export const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) ?? DEFAULT_LANGUAGE;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    void i18n.changeLanguage(languageCode);
    handleClose();
  };

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<LanguageIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        aria-label="Select language"
        aria-expanded={open}
        aria-haspopup="true"
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          fontSize: '0.95rem',
          fontWeight: 500,
          px: 2,
          py: 1,
          borderRadius: '8px',
          transition: 'all 0.3s',
          '&:hover': {
            color: 'text.primary',
            bgcolor: 'action.hover',
          },
        }}
      >
        {currentLanguage.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'background.paper',
              backdropFilter: 'blur(12px)',
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: '12px',
              mt: 1,
              minWidth: '150px',
            },
          },
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === i18n.language}
            sx={{
              color: 'text.primary',
              py: 1.5,
              px: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': {
                  bgcolor: 'action.selected',
                  opacity: 0.9,
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                sx={{
                  fontWeight: language.code === i18n.language ? 600 : 400,
                  color: 'text.primary',
                }}
              >
                {language.fullName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {language.label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
